import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';


export default function UpcomingClasses({ hideHeader = false, initialBatch = '', initialStudentId = '', selectedDate = null, hideDelete = false }) {
  const [liveClasses, setLiveClasses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(initialBatch);
  const [selectedStudentId, setSelectedStudentId] = useState(initialStudentId);
  const [loadingData, setLoadingData] = useState(true);
  const navigate = useNavigate();
  const { instituteId } = useParams();

  useEffect(() => {
    setSelectedBatch(initialBatch);
  }, [initialBatch]);

  useEffect(() => {
    setSelectedStudentId(initialStudentId);
  }, [initialStudentId]);

  const user = JSON.parse(localStorage.getItem('user') || "{}");
  const isInstitute = ['institute', 'SUPERADMIN', 'admin', 'ADMIN'].includes(user?.role);

  useEffect(() => {
    const headers = { 'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT' };
    
    // Get date in YYYY-MM-DD format
    const now = selectedDate || new Date();
    const targetDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [batchesRes, teachersRes, studentsRes] = await Promise.all([
          axios.get(API_BASE_URL + '/api/batches', { headers }),
          axios.get(API_BASE_URL + '/api/teachers', { headers }),
          isInstitute ? axios.get(API_BASE_URL + '/api/students', { headers }) : Promise.resolve({ data: [] })
        ]);

        const allBatches = Array.isArray(batchesRes.data) ? batchesRes.data : [];
        const allTeachers = Array.isArray(teachersRes.data) ? teachersRes.data : [];
        if (isInstitute) {
          setBatches(allBatches);
          if (Array.isArray(studentsRes.data)) {
            setStudents(studentsRes.data);
          } else {
            console.warn("API did not return an array for students in UpcomingClasses:", studentsRes.data);
            setStudents([]);
          }
        }

        let schedules = [];

        // 1. Process Master Schedules FIRST (Source of Truth with IDs)
        allBatches.forEach(b => {
          if (b.masterSchedule) {
            b.masterSchedule.forEach(sch => {
              if (sch.date === targetDateStr) {
                schedules.push({
                  id: sch.id, 
                  time: sch.time || `${sch.startTime} - ${sch.endTime}`,
                  subject: sch.subject,
                  topic: "Scheduled Lecture",
                  teacher: sch.teacherEmail || "Staff",
                  batchName: b.batchIdStr,
                  type: "Live App",
                  status: "Upcoming"
                });
              }
            });
          }
        });

        // 2. Extract from Teacher Profiles (Legacy/Sync fallback only if missing)
        allTeachers.forEach(t => {
          if (t.todaysSchedule) {
            t.todaysSchedule.forEach(sch => {
              if (!sch.topic || sch.topic.includes(targetDateStr)) {
                 const exists = schedules.find(s => s.batchName === sch.batchName && s.time === sch.timeSlot);
                 if (!exists) {
                    schedules.push({
                      id: null,
                      time: sch.timeSlot,
                      subject: sch.subject,
                      topic: sch.topic || "Class Session",
                      teacher: t.name,
                      batchName: sch.batchName,
                      type: "Live App",
                      status: sch.status || "Upcoming"
                    });
                 }
              }
            });
          }
        });
        
        // Sort by time
        schedules.sort((a,b) => {
           const get24H = (timeStr) => {
             if (!timeStr) return '00:00';
             let parts = timeStr.split(' - ')[0].trim().split(' ');
             if (parts.length < 2) return timeStr;
             const time = parts[0];
             const modifier = parts[1];
             let [hours, minutes] = time.split(':');
             if (hours === '12') hours = '00';
             if (modifier === 'PM') hours = String(parseInt(hours, 10) + 12);
             return `${hours.padStart(2, '0')}:${minutes}`;
           };
           return get24H(a.time).localeCompare(get24H(b.time));
        });
        
        setLiveClasses(schedules);
      } catch (err) {
        console.error("Failed to fetch upcoming classes", err);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedBatch, selectedStudentId]);

  const handleDelete = async (id, subject) => {
    if (!window.confirm(`Are you sure you want to delete the "${subject}" schedule? This cannot be undone.`)) return;

    try {
      const headers = { 'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT' };
      await axios.delete(`${API_BASE_URL}/api/batches/schedule/${id}`, { headers });
      
      // Update local state to remove the class immediately
      setLiveClasses(prev => prev.filter(cls => cls.id !== id));
      alert("Schedule deleted successfully.");
    } catch (err) {
      console.error("Failed to delete schedule", err);
      alert("Error deleting schedule. Please try again.");
    }
  };

  const filteredClasses = liveClasses.filter(cls => {
    if (!isInstitute) return true; // Standard view for students (or pre-filtered)
    
    // Admin filtering behavior
    if (selectedStudentId) {
      const student = students.find(s => s.id.toString() === selectedStudentId);
      if (student && cls.batchName !== student.batchIdStr) return false;
    } else if (selectedBatch) {
      if (cls.batchName !== selectedBatch) return false;
    }
    return true;
  });

  const handleJoin = (cls) => {
    if (!cls.batchName) {
      alert("Batch information missing. Cannot join class.");
      return;
    }
    // Create a URL-safe room name based on batch and subject
    const sanitizedBatch = String(cls.batchName).replace(/[^a-zA-Z0-9]/g, '_');
    const sanitizedSubject = (cls.subject || 'Lecture').replace(/[^a-zA-Z0-9]/g, '_');
    const roomName = `PenSense_${sanitizedBatch}_${sanitizedSubject}`;
    
    // Use absolute navigation to be 100% safe
    const targetPath = `/${instituteId}/lms/live-class/${cls.batchName}/${roomName}`;
    navigate(targetPath, { state: { from: window.location.pathname } });
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Header Banner */}
      {!hideHeader && (
        <div className="relative rounded-3xl bg-gradient-to-r from-violet-900/40 via-fuchsia-900/20 to-slate-900 border border-violet-500/20 p-8 overflow-hidden mb-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/10 blur-[80px] rounded-full"></div>
          <div className="absolute bottom-0 left-32 w-48 h-48 bg-fuchsia-500/10 blur-[60px] rounded-full"></div>
          
          <div className="relative z-10">
            <h1 className="text-3xl font-bold tracking-tight text-white mb-2 text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400">
              Upcoming Classes
            </h1>
            <p className="text-slate-400">View and join your daily live sessions based on your registered batches.</p>
            
            {isInstitute && (
              <div className="mt-6 flex flex-col sm:flex-row gap-4 max-w-2xl bg-slate-950/50 p-4 rounded-xl border border-violet-500/20 backdrop-blur-sm">
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs font-bold text-violet-300 uppercase tracking-wider ml-1">Filter by Batch</label>
                  <select 
                    className="w-full md:w-64 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-violet-500 transition-colors"
                    value={selectedBatch}
                    onChange={e => {
                      setSelectedBatch(e.target.value);
                      setSelectedStudentId('');
                    }}
                    disabled={loadingData}
                  >
                    <option value="">{loadingData ? "Loading Batches..." : "All Batches"}</option>
                    {batches.map(b => (
                      <option key={b.id} value={b.batchIdStr}>{b.name} ({b.batchIdStr})</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs font-bold text-fuchsia-300 uppercase tracking-wider ml-1">Pinpoint Student</label>
                  <select 
                    className="w-full md:w-64 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-fuchsia-500 transition-colors"
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value)}
                    disabled={!selectedBatch || loadingData}
                    title={!selectedBatch ? "Select a Batch first" : ""}
                  >
                    <option value="">{loadingData ? "Loading Students..." : "Specific Student..."}</option>
                    {(Array.isArray(students) ? students : []).filter(s => s.batchIdStr === selectedBatch).map(st => (
                      <option key={st.id} value={st.id}>{st.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={`rounded-3xl border border-violet-500/20 bg-slate-900/40 p-5 lg:p-6 flex flex-col ${hideHeader ? 'min-h-[300px]' : 'min-h-[500px]'}`}>
        <div className="flex items-center justify-between mb-4 lg:mb-6 shrink-0 border-b border-slate-800 pb-4">
          <h2 className="text-lg lg:text-xl font-bold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-500 flex items-center gap-2">
             {selectedDate ? (selectedDate.toDateString() === new Date().toDateString() ? "Today's Live Timetable" : `Timetable for ${selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`) : "Today's Live Timetable"}
          </h2>
          <span className="px-3 py-1 rounded-full bg-violet-500/10 text-violet-400 text-xs font-bold ring-1 ring-violet-500/20">{filteredClasses.length} Scheduled</span>
        </div>
        
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4">
          {filteredClasses.length === 0 ? (
            <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-slate-500 text-sm">
              <svg className="w-16 h-16 mb-4 text-slate-700 block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z"></path></svg>
              No classes successfully scheduled for your active timeline today.
            </div>
          ) : (
            filteredClasses.map((cls, idx) => (
              <div key={idx} className="group relative overflow-hidden p-5 rounded-2xl border border-slate-800 bg-slate-900/50 hover:bg-slate-800/80 hover:border-violet-500/30 transition-all duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                  <div className="flex items-center gap-5">
                    <div className="flex flex-col items-center justify-center w-20 h-20 rounded-2xl bg-slate-950 border border-slate-800 shrink-0 shadow-inner">
                      <span className="text-xs font-bold text-slate-500 mb-1 uppercase tracking-widest">Time</span>
                      <span className="text-sm font-bold text-slate-200 px-1 text-center leading-tight">{cls.time}</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-200 text-lg flex items-center gap-3">
                        {cls.subject} 
                        {cls.topic && <span className="text-slate-400 font-medium text-base">| {cls.topic}</span>}
                        {isInstitute && cls.batchName && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 ml-2">
                             {cls.batchName}
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-slate-400 mt-1.5 flex items-center gap-2 font-medium">
                        <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        Instructor: <span className="text-slate-300">{cls.teacher}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between md:flex-col md:items-end md:justify-center gap-3 w-full md:w-auto mt-4 md:mt-0 pt-4 md:pt-0 border-t border-slate-800 md:border-0 relative">
                    <span className="px-3 py-1 rounded bg-slate-950 text-xs font-bold uppercase tracking-widest text-slate-400 border border-slate-800 absolute top-[-14px] md:relative md:top-0 left-1/2 -translate-x-1/2 md:translate-x-0">
                      {cls.type || 'Live App'}
                    </span>
                    <button 
                      onClick={() => handleJoin(cls)}
                      className="w-full md:w-32 px-6 py-2.5 bg-violet-600 hover:bg-violet-500 hover:text-white text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-violet-900/20 active:scale-95"
                    >
                      Join Class
                    </button>
                    {!hideDelete && isInstitute && (
                      <button 
                        onClick={() => cls.id ? handleDelete(cls.id, cls.subject) : alert("This schedule was created before the Delete feature was added and lacks a unique ID. To enable deletion, please re-upload your timetable.")}
                        className="mt-2 text-xs font-bold text-slate-500 hover:text-red-400 flex items-center gap-1 transition-colors uppercase tracking-widest"
                      >
                        <svg className={`w-3.5 h-3.5 ${!cls.id ? 'opacity-30' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                        Delete {cls.id ? 'Schedule' : 'Lecture'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
